import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { SectionHeader, PrimaryLink, OutlineLink, OutlineLinkLight, FocusCard, ApproachSteps, OpportunityCards, CTABanner } from "@/components/Shared";
import { Compass, MapPin, Telescope, Lightbulb, ShieldCheck, MessagesSquare, Gem } from "lucide-react";
import { IMAGES } from "@/data/content";

const APPROACH_STEPS = [
  { num: "01", title: "Identify", description: "Finding locations and development opportunities with potential." },
  { num: "02", title: "Evaluate", description: "Understanding feasibility, location and development potential." },
  { num: "03", title: "Plan", description: "Creating thoughtful development strategies." },
  { num: "04", title: "Develop", description: "Working towards quality execution and responsible development." },
  { num: "05", title: "Create Value", description: "Building opportunities with a long-term perspective." },
];

const VALUES = [
  { icon: Lightbulb, title: "Strategic Thinking", description: "Evaluating opportunities with a long-term perspective." },
  { icon: ShieldCheck, title: "Responsible Development", description: "Creating development opportunities with purpose and thoughtfulness." },
  { icon: MessagesSquare, title: "Transparency", description: "Building relationships through clear and responsible communication." },
  { icon: MapPin, title: "Location Focus", description: "Paying attention to connectivity, surroundings and future potential." },
  { icon: Gem, title: "Quality Mindset", description: "Keeping usability, planning and quality at the centre of development." },
  { icon: Telescope, title: "Long-Term Vision", description: "Building Evaya Ventures as a sustainable real-estate development company." },
];

const EASE = [0.22, 1, 0.36, 1];
const HERO_LINES = ["Building Tomorrow's", "Real Estate Opportunities"];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  return (
    <>
      <SEO
        title="Evaya Ventures | Redevelopment & Plotted Land Development"
        description="Evaya Ventures is an emerging real-estate development company focused on redevelopment and thoughtfully planned plotted land development opportunities."
        path="/"
      />

      {/* Hero */}
      <section ref={heroRef} className="relative overflow-hidden bg-ink pt-40 lg:pt-48" data-testid="home-hero">
        <div aria-hidden="true" className="pointer-events-none absolute -right-24 top-1/4 h-[480px] w-[480px] rounded-full bg-brand/10 blur-[140px]" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
          <div className="grid grid-cols-1 items-center gap-14 pb-16 lg:grid-cols-12 lg:gap-10 lg:pb-24">
            <div className="lg:col-span-7">
              <motion.p
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
                className="text-xs font-semibold uppercase tracking-[0.32em] text-brand-light"
                data-testid="hero-overline"
              >
                From Earth to Extraordinary
              </motion.p>
              <h1 className="mt-6 font-display text-5xl leading-[1.04] tracking-tight text-white md:text-6xl lg:text-7xl" data-testid="hero-title">
                {HERO_LINES.map((line, i) => (
                  <span key={line} className="-mb-1 block overflow-hidden pb-2">
                    <motion.span
                      className="block"
                      initial={{ y: "112%" }}
                      animate={{ y: 0 }}
                      transition={{ duration: 1.05, delay: 0.25 + i * 0.14, ease: EASE }}
                    >
                      {line}
                    </motion.span>
                  </span>
                ))}
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
                className="mt-7 max-w-xl text-base leading-relaxed text-white/70 md:text-lg"
                data-testid="hero-description"
              >
                Evaya Ventures is an emerging real-estate development company focused on redevelopment and thoughtfully planned plotted land development.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
                className="mt-10 flex flex-wrap items-center gap-4"
              >
                <PrimaryLink to="/approach" testId="hero-explore-approach-button">Explore Our Approach</PrimaryLink>
                <OutlineLinkLight to="/contact" testId="hero-contact-button">Talk to Us</OutlineLinkLight>
              </motion.div>
            </div>
            <div className="lg:col-span-5">
              <motion.div
                initial={{ clipPath: "inset(100% 0 0 0)" }}
                animate={{ clipPath: "inset(0% 0 0 0)" }}
                transition={{ duration: 1.2, delay: 0.35, ease: EASE }}
                className="relative"
              >
                <div className="absolute -right-4 -top-4 h-full w-full border border-brand/50" aria-hidden="true" />
                <motion.div style={{ y: imgY, scale: imgScale }} className="relative">
                  <img
                    src={IMAGES.hero}
                    alt={IMAGES.heroAlt}
                    className="relative h-[420px] w-full object-cover lg:h-[560px]"
                    loading="eager"
                    fetchPriority="high"
                    data-testid="hero-image"
                  />
                </motion.div>
                <div className="absolute -bottom-6 -left-6 hidden border border-white/10 bg-olive px-7 py-5 text-white md:block" data-testid="hero-caption">
                  <p className="text-[11px] font-medium uppercase tracking-[0.28em] text-brand-light">Our Focus</p>
                  <p className="mt-1 font-display text-xl">Redevelopment &amp; Plotted Development</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
        <div className="relative border-t border-white/10 bg-olive">
          <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-white/10 px-6 sm:grid-cols-3 sm:divide-x sm:divide-y-0 lg:px-12" data-testid="focus-strip">
            {[
              { icon: Compass, label: "Redevelopment" },
              { icon: MapPin, label: "Plotted Land Development" },
              { icon: Telescope, label: "Future Development Opportunities" },
            ].map((item, i) => (
              <Reveal key={item.label} delay={i * 0.08} className="flex items-center gap-4 py-7 sm:justify-center">
                <item.icon className="h-6 w-6 shrink-0 text-brand-light" strokeWidth={1.5} aria-hidden="true" />
                <span className="text-sm font-medium tracking-wide text-white/80">{item.label}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      {/* About preview */}
      <section className="bg-white py-24 lg:py-32" data-testid="home-about-preview">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-12 lg:px-12">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -bottom-4 -left-4 h-full w-full border border-brand/40" aria-hidden="true" />
              <img src={IMAGES.planningHands} alt={IMAGES.planningHandsAlt} loading="lazy" className="relative h-[420px] w-full object-cover" />
            </div>
          </Reveal>
          <div className="lg:col-span-7 lg:pl-8">
            <SectionHeader
              overline="About Evaya Ventures"
              title="Creating Opportunities. Shaping Possibilities."
              testId="home-about"
            />
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-ink/65 md:text-lg">
                Evaya Ventures is an emerging real-estate development company focused on creating meaningful opportunities through redevelopment and plotted land development.
              </p>
              <p className="mt-4 text-base leading-relaxed text-ink/65">
                Our approach combines careful opportunity identification, thoughtful planning, responsible execution and a long-term perspective on real estate.
              </p>
              <p className="mt-6 font-display text-xl italic text-brand-dark md:text-2xl" data-testid="home-about-tagline">
                From Earth to Extraordinary.
              </p>
              <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="home-about-beliefs">
                {["Strategic location", "Thoughtful planning", "Quality execution", "Transparency", "Long-term value"].map((b) => (
                  <li key={b} className="flex items-center gap-3 border-b border-ink/10 pb-3 text-sm font-medium text-ink/75">
                    <span className="h-1.5 w-1.5 shrink-0 bg-brand" aria-hidden="true" />
                    {b}
                  </li>
                ))}
              </ul>
              <div className="mt-10">
                <OutlineLink to="/about" testId="home-about-cta">About Evaya Ventures</OutlineLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Our Focus */}
      <section className="border-t border-ink/10 bg-sand py-24 lg:py-32" data-testid="home-focus">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeader
            overline="Our Focus"
            title="Two paths. One development philosophy."
            description="We concentrate on redevelopment and plotted land development — two areas where careful planning and patient execution create lasting value."
            testId="home-focus"
          />
          <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-10">
            <FocusCard
              overline="Redevelopment"
              title="Reimagining Existing Spaces"
              copy="Evaya Ventures is exploring redevelopment opportunities where existing properties can be thoughtfully transformed into better planned, more functional and future-ready spaces."
              features={["Opportunity identification", "Feasibility", "Planning", "Stakeholder coordination", "Design strategy", "Development execution"]}
              image={IMAGES.redevelopmentCard}
              imageAlt={IMAGES.redevelopmentCardAlt}
              cta="Explore Redevelopment"
              to="/redevelopment"
              testId="focus-card-redevelopment"
            />
            <FocusCard
              overline="Plotted Land Development"
              title="Creating Better-Planned Land Opportunities"
              copy="Evaya Ventures focuses on thoughtfully planned plotted land opportunities, with attention to location, accessibility, infrastructure, surroundings and long-term value."
              features={["Land opportunity identification", "Layout planning", "Infrastructure", "Accessibility", "Landscaping", "Surroundings", "Long-term value"]}
              image={IMAGES.plottedCard}
              imageAlt={IMAGES.plottedCardAlt}
              cta="Explore Plotted Development"
              to="/plotted-development"
              testId="focus-card-plotted"
              delay={0.12}
            />
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="bg-white py-24 lg:py-32" data-testid="home-approach">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeader
            overline="From Earth to Extraordinary"
            title="From Potential to Possibility"
            description="Every opportunity we pursue moves through the same disciplined journey — from identification to long-term value creation."
            testId="home-approach"
          />
          <div className="mt-14">
            <ApproachSteps steps={APPROACH_STEPS} />
          </div>
          <Reveal delay={0.15} className="mt-10">
            <PrimaryLink to="/approach" testId="home-approach-cta">Explore Our Approach</PrimaryLink>
          </Reveal>
        </div>
      </section>

      {/* Why Evaya — manifesto chapters */}
      <section className="bg-ink py-24 lg:py-32" data-testid="home-why">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeader
            overline="Why Evaya Ventures"
            title="Principles that guide every decision"
            testId="home-why"
            dark
          />
          <div className="mt-14 grid grid-cols-1 gap-px border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-3">
            {VALUES.map((value, i) => (
              <div key={value.title} className="group relative overflow-hidden bg-ink">
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute -right-2 -top-8 select-none font-display text-[7rem] leading-none text-white/[0.05] transition-colors duration-500 group-hover:text-brand/15"
                >
                  0{i + 1}
                </span>
                <Reveal delay={i * 0.06} className="relative h-full p-8 lg:p-10">
                  <value.icon className="h-7 w-7 text-brand-light" strokeWidth={1.5} aria-hidden="true" />
                  <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.3em] text-brand-light/60">Chapter 0{i + 1}</p>
                  <h3 className="mt-2 font-display text-2xl text-white">{value.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">{value.description}</p>
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Developments preview */}
      <section className="bg-white py-24 lg:py-32" data-testid="home-developments-preview">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-12 lg:px-12">
          <div className="lg:col-span-6">
            <SectionHeader
              overline="Our Developments"
              title="Our development portfolio is evolving"
              description="We are currently evaluating and developing opportunities across redevelopment and plotted land development. Details of upcoming developments will be announced as they progress."
              testId="home-developments"
            />
            <Reveal delay={0.1}>
              <div className="mt-10">
                <PrimaryLink to="/developments" testId="home-developments-cta">View Developments</PrimaryLink>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="relative">
              <img src={IMAGES.concrete} alt={IMAGES.concreteAlt} loading="lazy" className="h-[380px] w-full object-cover" />
              <div className="absolute bottom-0 left-0 border border-ink/10 bg-white px-7 py-5" data-testid="home-developments-caption">
                <p className="text-[11px] font-medium uppercase tracking-[0.28em] text-brand-dark">Upcoming Opportunities</p>
                <p className="mt-1 font-display text-xl text-ink">Announcements as developments progress</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Opportunities */}
      <section className="bg-ink py-24 lg:py-32" data-testid="home-opportunities">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeader
            overline="Have a Development Opportunity?"
            title="Let's evaluate it together"
            description="Evaya Ventures is open to evaluating suitable redevelopment and land development opportunities."
            testId="home-opportunities"
            dark
          />
          <div className="mt-14">
            <OpportunityCards />
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
};

export default Home;
